/**
 * RBAC Permission Hooks for SkyAdvisor
 *
 * These hooks provide easy access to check user permissions at the component level.
 * They combine admin hierarchy checks with granular RBAC permissions.
 *
 * ══ TWO VOCABULARIES LIVE IN THIS FILE. THEY ARE NOT THE SAME THING. ════════
 *
 * 1. THE PLATFORM VOCABULARY — `usePermissionState().isAdmin`,
 *    `useHasPermission(...)`, `useCanManage(...)`. `isAdmin` here means
 *    **platform OR tenant OR instance admin** (`useAuthPermissions`), and every
 *    permission hook in this family ADMIN-BYPASSES: an admin answers `true`
 *    without the resolved permission map being consulted at all. The server
 *    counterpart is `requirePermission(...)` (~44 route files).
 *
 * 2. THE CRM VOCABULARY — `useCrmRoleState()` and its `isCrmAdmin` /
 *    `isCrmManagerOrAdmin` predicates. This is the CRM role stored on
 *    `skycrm_user_person_link.role` (counselor | manager | admin), resolved
 *    SERVER-side and delivered by `GET /crm/me`. The server counterpart is
 *    `requireCrmRole(...)` (11 route files) plus a handful of in-handler
 *    `req.crmRole !== "admin"` checks.
 *
 * ⚠️ A PERSON CAN HOLD ONE AND NOT THE OTHER, AND THAT IS THE WHOLE POINT. A
 * platform or tenant admin whose CRM role is `counselor` answers `isAdmin ===
 * true` and `isCrmAdmin === false`. Gating a CRM surface on the platform notion
 * therefore renders a page whose every request 403s — the defect this file's
 * split exists to prevent (found as G18 case CFG-009: a dead "New Destination"
 * button beside the lie "No delivery destinations yet").
 *
 * SO: **check the vocabulary the SERVER checks.** If the route is
 * `requireCrmRole`, ask `useCrmRoleState`. If it is `requirePermission`, ask
 * `useHasPermission`. Never substitute one for the other because the names
 * rhyme. `src/hooks/__tests__/crmAdminSurfaceGates.test.ts` enforces this and
 * will fail the suite if a new CRM surface picks the wrong one.
 *
 * Both families are RENDER HINTS ONLY. The server re-resolves on every request
 * and is the enforcement point; nothing here may be weakened to make a client
 * and a server agree — the client moves to match the server, never the reverse.
 */

import { useGetMyPermissionsQuery } from "../store/api/rbacApi";
import { useGetCrmMeQuery, type CrmRole } from "../store/api/crmMeApi";
import { isCrmForbiddenError } from "../store/api/crmForbidden";
import { useAuthPermissions } from "./useAuth";
import { authService } from "../utils/authService";
import { getInstanceIdFromUrl } from "../utils/instanceId";

/**
 * Get the current instance ID for permission queries
 */
const useInstanceId = (): string | undefined => {
  // Get instanceId from URL or localStorage
  return authService.getInstanceId() || undefined;
};

/**
 * Hook to check if user has a specific RBAC permission
 * @param permission - The permission key to check (e.g., 'manageLeads', 'viewCampaigns')
 * @returns boolean indicating if user has the permission
 */
export const useHasPermission = (permission: string): boolean => {
  const instanceId = useInstanceId();
  const { data: myPermissions, isLoading } = useGetMyPermissionsQuery(
    instanceId ? { instanceId } : undefined,
  );
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = useAuthPermissions();

  // Admins bypass all permission checks
  if (isPlatformAdmin || isTenantAdmin || isInstanceAdmin) {
    return true;
  }

  // While loading, deny access to be safe
  if (isLoading || !myPermissions?.permissions) {
    return false;
  }

  return myPermissions.permissions[permission] ?? false;
};

/**
 * Hook to check if user has ANY of the specified permissions
 * @param permissions - Array of permission keys to check
 * @returns boolean indicating if user has at least one of the permissions
 */
export const useHasAnyPermission = (permissions: string[]): boolean => {
  const instanceId = useInstanceId();
  const { data: myPermissions, isLoading } = useGetMyPermissionsQuery(
    instanceId ? { instanceId } : undefined,
  );
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = useAuthPermissions();

  // Admins bypass all permission checks
  if (isPlatformAdmin || isTenantAdmin || isInstanceAdmin) {
    return true;
  }

  // While loading, deny access to be safe
  if (isLoading || !myPermissions?.permissions) {
    return false;
  }

  return permissions.some((p) => myPermissions.permissions[p]);
};

/**
 * Hook to check if user has ALL of the specified permissions
 * @param permissions - Array of permission keys to check
 * @returns boolean indicating if user has all of the permissions
 */
export const useHasAllPermissions = (permissions: string[]): boolean => {
  const instanceId = useInstanceId();
  const { data: myPermissions, isLoading } = useGetMyPermissionsQuery(
    instanceId ? { instanceId } : undefined,
  );
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = useAuthPermissions();

  // Admins bypass all permission checks
  if (isPlatformAdmin || isTenantAdmin || isInstanceAdmin) {
    return true;
  }

  // While loading, deny access to be safe
  if (isLoading || !myPermissions?.permissions) {
    return false;
  }

  return permissions.every((p) => myPermissions.permissions[p]);
};

/**
 * Hook to check if user can manage a feature (admin OR specific permission)
 * @param feature - The feature name (will check for 'manage{Feature}' permission)
 * @returns boolean indicating if user can manage the feature
 */
export const useCanManage = (feature: string): boolean => {
  const permissionKey = `manage${feature}`;
  return useHasPermission(permissionKey);
};

/**
 * Hook to check if user can view a feature (admin OR specific permission)
 * @param feature - The feature name (will check for 'view{Feature}' permission)
 * @returns boolean indicating if user can view the feature
 */
export const useCanView = (feature: string): boolean => {
  const permissionKey = `view${feature}`;
  return useHasPermission(permissionKey);
};

/**
 * Hook to get all permissions with loading state
 * @returns Object with permissions, loading state, and admin flags
 */
export const usePermissionState = () => {
  const instanceId = useInstanceId();
  const {
    data: myPermissions,
    isLoading,
    isError,
  } = useGetMyPermissionsQuery(instanceId ? { instanceId } : undefined);
  const { isPlatformAdmin, isTenantAdmin, isInstanceAdmin } = useAuthPermissions();

  const isAdmin = isPlatformAdmin || isTenantAdmin || isInstanceAdmin;

  return {
    permissions: myPermissions?.permissions ?? {},
    navigation: myPermissions?.navigation ?? {},
    role: myPermissions?.role,
    roleLabel: myPermissions?.roleLabel,
    allowedInstances: myPermissions?.allowedInstances,
    instanceScope: myPermissions?.instanceScope,
    isLoading,
    isError,
    isAdmin,
    isPlatformAdmin,
    isTenantAdmin,
    isInstanceAdmin,
  };
};

// ════════════════════════════════════════════════════════════════════════════
// THE CRM VOCABULARY — deliberately named so no call site can confuse it with
// the platform one above. Everything here is `crmRole` / `isCrm…`; nothing here
// is called `isAdmin`, and `usePermissionState().isAdmin` keeps its existing
// platform/tenant/instance meaning untouched (non-CRM surfaces rely on it).
// ════════════════════════════════════════════════════════════════════════════

export type { CrmRole };

/**
 * THE THREE THINGS `GET /crm/me` CAN LEAVE YOU WITH, kept apart because two of
 * them used to be one ([FT-CRM-ROLE-UNKNOWN]).
 *
 * `"loading"`  — no answer YET. Nothing may be granted and nothing may be
 *                CLAIMED: a surface that renders a refusal here flashes "you
 *                don't have permission" at the admin who does, on every cold
 *                load.
 * `"resolved"` — the server ANSWERED, and `crmRole` is that answer. `null` is
 *                a real answer too ("no CRM identity in this instance"), and
 *                the server treats it as refused, so a refusal state is honest
 *                here.
 * `"failed"`   — the request did not come back with an answer at all (500,
 *                offline, timeout, a proxy's HTML error page). The role is
 *                UNKNOWN and cannot be inferred. A refusal state here is a LIE
 *                about the caller caused by an outage: it tells an entitled CRM
 *                admin to go and ask a CRM admin for access.
 *
 * ⚠️ THE SPLIT BETWEEN `"resolved"` AND `"failed"` IS THE SAME ONE
 * `isCrmForbiddenError` DRAWS ONE LAYER DOWN, and it is drawn here BY that
 * function rather than beside it: a 403 is a permission ANSWER, so it resolves
 * (to a refusal); a 500 / `FETCH_ERROR` / `TIMEOUT_ERROR` is not an answer at
 * all, so it fails. Do not add a fourth state or a second predicate — the whole
 * defect this replaces was two different things sharing one falsy value.
 *
 * ⚠️ `"failed"` STILL FAILS CLOSED ON ACTIONS. `isCrmAdmin` and
 * `isCrmManagerOrAdmin` are `false` in it, exactly as in `"loading"`, because
 * the server enforces regardless and a granted-on-error affordance would only
 * re-create the dead buttons this packet removed. What changes is the CLAIM the
 * surface is allowed to make, not the permission it may assume.
 */
export type CrmRoleStatus = "loading" | "resolved" | "failed";

export interface CrmRoleState {
  /**
   * The caller's CRM role for this instance — the EXACT value `requireCrmRole`
   * gates on server-side. `null` means "no CRM identity in this instance",
   * which the server also treats as refused.
   *
   * ⚠️ `null` ALSO READS OUT OF A `"failed"` STATE, where it means "we could not
   * find out" rather than "you have none". Branch on `crmRoleStatus`, never on
   * `crmRole === null` alone.
   */
  crmRole: CrmRole | null;
  /** Which of the three answers above we are holding. See `CrmRoleStatus`. */
  crmRoleStatus: CrmRoleStatus;
  /** CRM role === "admin". The client mirror of `requireCrmRole("admin")`. */
  isCrmAdmin: boolean;
  /** The client mirror of `requireCrmRole("manager", "admin")`. */
  isCrmManagerOrAdmin: boolean;
  /**
   * `crmRoleStatus === "loading"` — the answer is still in flight. Use it to
   * avoid FLASHING a refusal at someone who turns out to be entitled — never to
   * grant anything: every predicate above is already `false` while this is
   * `true` (fail closed).
   */
  isCrmRoleLoading: boolean;
  /**
   * `crmRoleStatus === "failed"` — `/crm/me` did not answer, so the role is
   * unknown. Render an ERROR / RETRY state, **never** a permission-denied state:
   * the caller may well be entitled and the only thing that went wrong is the
   * backend. Grants nothing, exactly like `isCrmRoleLoading`.
   */
  isCrmRoleUnavailable: boolean;
  /**
   * Ask `/crm/me` again. Pair it with `isCrmRoleUnavailable` so the error state
   * offers a way out instead of requiring a page reload. A no-op when there is
   * no instance id to ask about.
   */
  retryCrmRole: () => void;
}

/**
 * The caller's **CRM role** for an instance (`skycrm_user_person_link.role`).
 *
 * ⚠️ THIS IS NOT `usePermissionState().isAdmin`. That one is the
 * platform/tenant/instance hierarchy and it admin-bypasses; this one is the CRM
 * role and it does not. See the file header — conflating the two is the root
 * cause this hook exists to remove, so the names are kept deliberately
 * unlike each other.
 *
 * Reads `GET /crm/me` (`crmMeApi`), which resolves the role server-side. That
 * is the same source `ReportsHubPage` and `OverviewPage` already used correctly
 * before this hook existed; they are the in-repo templates.
 *
 * @param instanceId Pass the SAME instance id the surface's own queries use, so
 * the role answer and the data answer can never be about different instances.
 * Falls back to `getInstanceIdFromUrl()` when omitted.
 * @param options.skip Do not ask `/crm/me` at all (a surface whose role comes
 * from elsewhere, e.g. the Advisor schedule dialog's `viewer` block — `/crm/me`
 * is operator-gated and 403s on an advisor-only tenant). Reads as case 4 below:
 * resolved, `crmRole === null`, nothing granted.
 *
 * RENDER HINT ONLY. The server re-resolves the role on every request and is the
 * enforcement point; a surface must still take a 403 as the authoritative
 * answer (see `isCrmForbiddenError` + `CrmPermissionState`), because the role
 * can change — or be cached stale by `lookupCrmPersonContext` — between the
 * render and the request.
 *
 * THREE-WAY, NOT TWO-WAY. See `CrmRoleStatus`. The hook used to answer a bare
 * `false` for both "resolved: you are a counselor" and "we could not ask", so
 * every surface downstream said "you don't have permission" when the backend
 * hiccupped — a statement about the CALLER caused by an OUTAGE, which is the
 * same conflation `isCrmForbiddenError` exists to prevent one layer down.
 */
export const useCrmRoleState = (
  instanceId?: string,
  options?: { skip?: boolean },
): CrmRoleState => {
  const skipped = options?.skip === true;
  const resolvedInstanceId = skipped ? "" : (instanceId ?? getInstanceIdFromUrl() ?? "");
  const { data, isLoading, isFetching, isError, error, refetch } = useGetCrmMeQuery(
    { instanceId: resolvedInstanceId },
    { skip: !resolvedInstanceId },
  );

  const crmRole = data?.crm_role ?? null;

  // The order of these branches is the meaning, so it is spelled out rather
  // than compressed:
  //
  // 1. WE HAVE A PAYLOAD → resolved, whatever happened since. RTK Query keeps
  //    the last good `data` when a re-fetch fails, and a stale-but-real answer
  //    beats claiming we have none; the 403 handling on each surface is what
  //    catches a role that changed underneath us.
  // 2. AN ERROR AND NO PAYLOAD → ask `isCrmForbiddenError`, the ONE predicate
  //    for this question. 403 = the server answered "not you" (there is no CRM
  //    identity here to have a role) → resolved, refused. Anything else — 500,
  //    FETCH_ERROR, TIMEOUT_ERROR, a proxy's HTML page — is not an answer, so
  //    the role is unknown → failed. 401 lands in `failed` too and that is
  //    deliberate: an expired session is an AUTHENTICATION problem the app's
  //    auth layer owns and will act on, and it is certainly not a statement
  //    that this person lacks a CRM role.
  // 3. IN FLIGHT → loading.
  // 4. NEITHER, WHICH MEANS THE QUERY IS SKIPPED for want of an instance id →
  //    resolved with `crmRole === null`. There is no instance in which to hold
  //    a role, so this is an answer and not an outage; it is also exactly what
  //    this hook did before the third state existed, and no surface's behaviour
  //    moves because of it.
  let crmRoleStatus: CrmRoleStatus;
  if (data !== undefined) {
    crmRoleStatus = "resolved";
  } else if (isError) {
    crmRoleStatus = isCrmForbiddenError(error) ? "resolved" : "failed";
  } else if (isLoading || isFetching) {
    crmRoleStatus = "loading";
  } else {
    crmRoleStatus = "resolved";
  }

  return {
    crmRole,
    crmRoleStatus,
    // Unchanged, and deliberately so: both non-`resolved` states grant nothing.
    // The three-way split governs what a surface may SAY, not what it may DO.
    isCrmAdmin: crmRole === "admin",
    isCrmManagerOrAdmin: crmRole === "admin" || crmRole === "manager",
    isCrmRoleLoading: crmRoleStatus === "loading",
    isCrmRoleUnavailable: crmRoleStatus === "failed",
    retryCrmRole: () => {
      // `refetch()` on a skipped query warns and does nothing useful, so the
      // no-instance case is short-circuited rather than left to RTK.
      if (resolvedInstanceId) refetch();
    },
  };
};

/**
 * Permission type definitions for type safety
 */
export type PermissionKey =
  // Conversations
  | "viewInbox"
  | "manageInbox"
  | "replyConversations"
  | "viewCallLogs"
  | "viewTraining"
  // Leads
  | "viewLeads"
  | "manageLeads"
  | "assignLeads"
  | "exportLeads"
  | "viewLeadIntelligence"
  // Tours
  | "viewTours"
  | "manageTours"
  | "manageTourSettings"
  // Campaigns
  | "viewCampaigns"
  | "manageCampaigns"
  | "publishCampaigns"
  // Landing Pages
  | "viewLandingPages"
  | "manageLandingPages"
  | "publishLandingPages"
  // Forms
  | "viewForms"
  | "manageForms"
  | "viewFormSubmissions"
  // Content Hub
  | "viewContent"
  | "manageContent"
  // Assessments & Surveys
  | "viewAssessments"
  | "manageAssessments"
  | "viewSurveys"
  | "manageSurveys"
  // AI Sales Agent
  | "viewASA"
  | "manageASA"
  | "manageASAKnowledgeBase"
  // Widget
  | "viewWidget"
  | "manageWidget"
  // Analytics
  | "viewAnalytics"
  | "viewAttributionAnalytics"
  | "viewCallAnalytics"
  | "manageCostTracking"
  // Settings
  | "viewSettings"
  | "manageSettings"
  // ⚠️ `clinical_read` IS DELIBERATELY ABSENT FROM THIS UNION, and that is not
  // an oversight (G9 Inc-3 / F14 disposition, 2026-08-14).
  //
  // Every hook in this file ADMIN-BYPASSES: `useHasPermission` returns true for
  // any platform/tenant/instance admin without consulting the resolved
  // permission map at all. `clinical_read` is a consumer-health-data gate whose
  // whole premise is that a role which does not EXPLICITLY grant it cannot read
  // clinical fields — so answering it through a bypassing hook would be
  // permissive in exactly the way the disposition forbids.
  //
  // The server is the authority and there is no client-side clinical gate: the
  // resident-detail payload already carries `care_plan_restricted_fields`, and
  // the UI renders from THAT (see `components/crm/carePlanWithheld.tsx`). If a
  // future surface needs to know, read the marker — do NOT call
  // `useHasPermission("clinical_read")`. The role EDITOR renders the key from a
  // plain string list and never asks these hooks about it.
  // Users
  | "viewUsers"
  | "manageUsers"
  | "assignRoles"
  // Locations
  | "viewLocations"
  | "manageLocations"
  // Platform
  | "manageOperators"
  | "manageOwners"
  | "manageInstances"
  | "manageTenants";

/**
 * Typed version of useHasPermission for better IDE support
 */
export const useHasTypedPermission = (permission: PermissionKey): boolean => {
  return useHasPermission(permission);
};
